import * as SwitchPrimitives from "@radix-ui/react-switch";
import { type FC, useEffect, useState } from "react";
import { cn, type PropsWithClassName } from "./podkit/lib/cn";

export type SwitchState = "unchecked" | "pending" | "checked";

export type SwitchProps = {
    state: SwitchState;
    disabled?: boolean;
    onToggle: (checked: boolean) => Promise<void> | void;
    fixedWidth?: boolean; // When true, the switch doesn't change width when animating.
    id?: string;
    "aria-label"?: string;
};

export const Switch: FC<SwitchProps> = ({ state, disabled, onToggle, fixedWidth, id, "aria-label": ariaLabel }) => {
    // This ensures we only animate state transitions, and not the initial render.
    const [initialState] = useState(state);
    const [hasTransitioned, setHasTransitioned] = useState(false);
    useEffect(() => {
        if (state !== initialState) {
            setHasTransitioned(true);
        }
    }, [state, initialState]);
    const isPending = state === "pending";
    const animate = hasTransitioned || isPending || state !== initialState;

    return (
        <div className="flex w-9 justify-center">
            <SwitchPrimitives.Root
                checked={state === "checked"}
                disabled={disabled || isPending}
                onCheckedChange={onToggle}
                aria-label={ariaLabel}
                className={cn("cursor-pointer rounded-full bg-surface-accent disabled:cursor-default", "h-5 w-9", {
                    "transition-[width] duration-300": animate,
                    "w-5": isPending && !fixedWidth,
                    "data-[state=checked]:bg-content-success": !isPending,
                    "data-[state=checked]:disabled:bg-content-success": !isPending,
                    "opacity-50": disabled && !isPending,
                })}
                id={id}
                data-testid={id}
            >
                <SwitchPrimitives.Thumb
                    className={cn("flex size-5 items-center justify-center", {
                        "transition-transform duration-300": animate,
                        "data-[state=checked]:translate-x-[16px]": !isPending,
                    })}
                >
                    <ThumbSVG className="size-5" animating={isPending} />
                </SwitchPrimitives.Thumb>
            </SwitchPrimitives.Root>
        </div>
    );
};

const ThumbSVG: FC<PropsWithClassName<{ animating: boolean }>> = ({ className, animating }) => {
    /**
     * Implementation notes:
     * The basic idea is to have a circle with a partial stroke and have it spin using CSS. The stroke is transparent
     * when the thumb is not "loading".
     *
     * You can't define if a border should be drawn "outside" of a circle, so to have full control of the border we're
     * using two circle: one for the thumb and one for the border. The border-circle is transparent and uses a stroke with
     * a stroke-dasharray to achieve the "partial border" effect.
     *
     * Note the two shadow definitions here:
     * - The first is the gitpod-themed shadow for the thumb
     * - The second is the Ona-themed shadow for the thumb. It maps to shadow-lg in tailwind,
     *   but unfortunately it's not possible to apply tailwind classes to svg elements.
     *
     * We define two thumbs that use the corresponding shadow definition in the svg,
     * and hide the corresponding one depending on whether ona theme is active.
     */
    return (
        <svg
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cn({ "animate-spin text-content-yield duration-1000": animating }, className)}
        >
            <g filter="url(#filter0_d_2076_8655)" className="hidden">
                <circle cx="12.5" cy="12.5" r={10} fill="rgb(var(--surface-glass))" />
            </g>
            <g filter="url(#filter0_dd_3651_33627)">
                <circle cx="12.5" cy="12.5" r={10} fill="rgb(var(--ona-white))" />
            </g>
            <circle
                cx="12.5"
                cy="12.5"
                r={11.5}
                fill="none"
                stroke={animating ? "currentColor" : "transparent"}
                strokeWidth={2}
                // 18 is the length of the stroke
                // 76 is the circumference of the circle to ensure we only render "one" stroke
                strokeDasharray={"18 76"}
            />
            {/* Drop-shadow effect copied from Figma */}
            <defs>
                <filter
                    id="filter0_d_2076_8655"
                    x="0"
                    y="0"
                    width="24"
                    height="24"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="1" />
                    <feGaussianBlur stdDeviation="1" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2076_8655" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2076_8655" result="shape" />
                </filter>
            </defs>

            {/* Ona Drop-shadow effect copied from Figma */}
            <filter
                id="filter0_dd_3651_33627"
                x="0.408447"
                y="0"
                width="40"
                height="40"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
            >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                />
                <feMorphology radius="2" operator="erode" in="SourceAlpha" result="effect1_dropShadow_3651_33627" />
                <feOffset dy="4" />
                <feGaussianBlur stdDeviation="3" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3651_33627" />
                <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                />
                <feMorphology radius="3" operator="erode" in="SourceAlpha" result="effect2_dropShadow_3651_33627" />
                <feOffset dy="10" />
                <feGaussianBlur stdDeviation="7.5" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                <feBlend mode="normal" in2="effect1_dropShadow_3651_33627" result="effect2_dropShadow_3651_33627" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_3651_33627" result="shape" />
            </filter>
        </svg>
    );
};
